import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Coins, Zap, ShieldCheck, Loader2, Infinity as InfinityIcon, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { rechargeCredits, fetchCreditBalance } from '../apiLayer/dashBoardAPIs/vsCodeDashboardAPIs';
// TODO(lifetime-agreement): disabled until backend ships POST /payment/send_lifetime_invoice
// import LifetimeAgreementModal from '../components/LifetimeAgreementModal';
import { local } from '../utils/constants';

const serverBaseUrl = local ? "http://127.0.0.1:8000" : "https://backend.withsix.co";

const PRESET_AMOUNTS = [10, 25, 50, 100];
const MIN_AMOUNT = 1;
const MAX_AMOUNT = 500;

const BuyCredits = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('status') === 'cancelled';

  const [loadingUser, setLoadingUser] = useState(true);
  const [isUltra, setIsUltra] = useState(false);
  // TODO(lifetime-agreement): disabled until backend ships POST /payment/send_lifetime_invoice
  // const [userEmail, setUserEmail] = useState('');
  // const [showLifetimeAgreement, setShowLifetimeAgreement] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [amount, setAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('web_access_token') || localStorage.getItem('jwt_token');
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        const response = await fetch(`${serverBaseUrl}/auth/get_user_info`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (response.ok) {
          const userData = await response.json();
          setIsUltra(userData?.users_plan_name === 'ultra');
        }
        const balance = await fetchCreditBalance();
        setCreditBalance(balance?.credit_balance ?? 0);
      } catch (e) {
        console.error('BuyCredits init error:', e);
      } finally {
        setLoadingUser(false);
      }
    };
    init();
  }, [navigate]);

  const effectiveAmount = customAmount !== '' ? parseFloat(customAmount) : amount;
  const amountValid = !isNaN(effectiveAmount) && effectiveAmount >= MIN_AMOUNT && effectiveAmount <= MAX_AMOUNT;

  const handleBuy = async () => {
    if (!amountValid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const origin = window.location.origin;
      const result = await rechargeCredits(
        effectiveAmount,
        `${origin}/dashboard`,
        `${origin}/buy-credits?status=cancelled`
      );
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
      } else {
        setError('Could not start checkout. Please try again.');
        setSubmitting(false);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === 'ultra_only') {
        setError('Credit recharge is only available on the Ultra plan.');
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Could not start checkout. Please try again.');
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] p-8">
      <div className="max-w-xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-full bg-white/5 border border-white/10">
            <Coins className="w-5 h-5 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-white">Buy Credits</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">
          Extra credits kick in automatically when your included Ultra budget is spent — powered by our most capable model.
        </p>

        {cancelled && (
          <Card className="mb-6 border-yellow-500/30 bg-yellow-500/5">
            <CardContent className="p-4 text-sm text-yellow-200">
              Checkout was cancelled. No payment was made.
            </CardContent>
          </Card>
        )}

        {loadingUser ? (
          <Card className="bg-[#121219]/80 border-white/10">
            <CardContent className="flex items-center justify-center p-12">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </CardContent>
          </Card>
        ) : !isUltra ? (
          <Card className="bg-[#121219]/80 border-white/10">
            <CardContent className="p-8 text-center">
              <Zap className="w-8 h-8 text-primary mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Ultra exclusive</h3>
              <p className="text-muted-foreground mb-6">
                Credit recharge is only available on the Ultra plan. Upgrade to Ultra to unlock extra credits —
                or skip metering entirely with an Unlimited plan.
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Button variant="outline" onClick={() => navigate('/pricing')}>View Plans</Button>
                <Button
                  onClick={() => navigate('/pricing#lifetime')}
                  className="bg-gradient-to-r from-amber-300 to-amber-500 hover:opacity-90 text-amber-950 border-0 gap-2 font-semibold"
                >
                  <InfinityIcon className="w-4 h-4" /> Go Unlimited
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="mb-6 bg-[#121219]/80 border-white/10">
              <CardContent className="flex items-center justify-between p-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Current credit balance</p>
                  <p className="text-3xl font-bold text-white">
                    ${(creditBalance ?? 0).toFixed(2)}
                  </p>
                </div>
                <ShieldCheck className="w-6 h-6 text-green-500" />
              </CardContent>
            </Card>

            {/* Upgrade-to-Lifetime CTA — buying credits repeatedly adds up; pitch
                the one-off unlimited plan. Gold treatment, on-brand with the
                amber accents used elsewhere on this screen. */}
            <Card className="mb-6 border-amber-400/30 bg-gradient-to-r from-amber-400/10 via-amber-400/[0.04] to-transparent">
              <CardContent className="flex items-center justify-between gap-4 p-6 flex-wrap">
                <div className="flex items-start gap-4 min-w-0">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
                    <InfinityIcon className="w-5 h-5 text-amber-300" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
                      Topping up adds up fast.
                      <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300 border border-amber-400/30">
                        Best value
                      </span>
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Go unlimited for <span className="font-semibold text-amber-200">$1,250/year</span>, or{' '}
                      <span className="font-semibold text-amber-200">$1,750 once</span> for life. Zero usage caps, no
                      rate limits, and your current plan is credited. Stop buying credits.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/pricing#lifetime')}
                  className="flex-shrink-0 bg-gradient-to-r from-amber-300 to-amber-500 hover:opacity-90 text-amber-950 border-0 gap-2 font-semibold"
                >
                  Go Unlimited <ArrowRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-[#121219]/80 border-white/10">
              <CardContent className="p-6">
                <p className="text-sm font-medium text-white mb-4">Choose an amount</p>

                <div className="grid grid-cols-4 gap-3 mb-4">
                  {PRESET_AMOUNTS.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => { setAmount(preset); setCustomAmount(''); }}
                      className={`py-3 rounded-lg border text-sm font-semibold transition-all ${
                        customAmount === '' && amount === preset
                          ? 'border-primary bg-primary/10 text-white'
                          : 'border-white/10 bg-white/5 text-gray-300 hover:border-white/30'
                      }`}
                    >
                      ${preset}
                    </button>
                  ))}
                </div>

                <div className="mb-6">
                  <label className="block text-xs text-muted-foreground mb-2">
                    Or enter a custom amount (${MIN_AMOUNT} – ${MAX_AMOUNT})
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                      type="number"
                      min={MIN_AMOUNT}
                      max={MAX_AMOUNT}
                      step="1"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder={`${amount}`}
                      className="w-full bg-white/5 border border-white/10 rounded-lg py-3 pl-8 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-primary"
                    />
                  </div>
                  {customAmount !== '' && !amountValid && (
                    <p className="text-xs text-red-400 mt-2">
                      Enter an amount between ${MIN_AMOUNT} and ${MAX_AMOUNT}.
                    </p>
                  )}
                </div>

                {error && (
                  <p className="text-sm text-red-400 mb-4">{error}</p>
                )}

                <Button
                  onClick={handleBuy}
                  disabled={!amountValid || submitting}
                  className="w-full py-6 text-base font-semibold"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Redirecting to checkout…
                    </span>
                  ) : (
                    `Buy $${amountValid ? effectiveAmount : amount} in Credits`
                  )}
                </Button>

                <p className="text-xs text-muted-foreground text-center mt-4">
                  Secure checkout with Stripe. Credits never expire and are used only after your included budget runs out.
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* TODO(lifetime-agreement): disabled until backend ships
          POST /payment/send_lifetime_invoice — CTAs above navigate to
          /pricing#lifetime again in the meantime.
      <LifetimeAgreementModal
        open={showLifetimeAgreement}
        onClose={() => setShowLifetimeAgreement(false)}
        email={userEmail}
      />
      */}
    </div>
  );
};

export default BuyCredits;
